import React, { useState, useEffect, useContext } from 'react';
import { userService } from '../services/apiClient';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import {
  User as UserIcon,
  Mail,
  Hash,
  BookOpen,
  GraduationCap,
  Phone,
  Camera,
  Calendar,
  Users,
  Edit3,
  Save,
  X,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const { user: authUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await userService.getUserProfile();
      // Backend returns the user object directly based on previous view_file
      const userData = response.data;
      setProfile(userData);
      setFormData({
        phone: userData.phone || '',
        profilePicture: userData.profilePicture || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await userService.updateProfile(formData);
      setEditMode(false);
      fetchProfile();
      // Proactive: Update would usually be shown by toast, using alert as fallback
    } catch (error) {
      alert('Failed to update profile');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />

      {/* Hero Header */}
      <div className="h-64 bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 relative">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-6xl mx-auto px-4 h-full flex items-end pb-12 relative z-10">
          <div className="flex flex-col md:flex-row items-center md:items-end gap-6 text-center md:text-left">
            <div className="relative group">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-[2.5rem] border-4 border-white shadow-2xl overflow-hidden bg-white">
                {profile.profilePicture ? (
                  <img src={profile.profilePicture} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-purple-100 flex items-center justify-center text-purple-600">
                    <UserIcon size={64} />
                  </div>
                )}
              </div>
              {editMode && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="text-white w-8 h-8" />
                </div>
              )}
            </div>
            <div className="mb-2">
              <span className="bg-white/20 backdrop-blur-md text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/30 mb-2 inline-block">
                {profile.role}
              </span>
              <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">{profile.name}</h1>
              <p className="text-blue-100 font-medium mt-1 flex items-center justify-center md:justify-start gap-2">
                <Mail className="w-4 h-4" /> {profile.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 -mt-8 relative z-20 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-[2rem] shadow-xl shadow-purple-900/5 p-8 border border-gray-100">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                  <span className="bg-purple-100 p-2 rounded-xl text-purple-600"><Hash className="w-5 h-5" /></span>
                  Academic Profile
                </h2>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold transition-all ${editMode
                      ? 'bg-red-50 text-red-600 hover:bg-red-100'
                      : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                    }`}
                >
                  {editMode ? <><X className="w-4 h-4" /> Cancel</> : <><Edit3 className="w-4 h-4" /> Edit Profile</>}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                {[
                  { label: 'Roll Number', value: profile.rollNumber, icon: <Hash /> },
                  { label: 'College Branch', value: profile.branch, icon: <BookOpen /> },
                  { label: 'Current Year', value: `${profile.year}${profile.year === 1 ? 'st' : profile.year === 2 ? 'nd' : profile.year === 3 ? 'rd' : 'th'} Year`, icon: <GraduationCap /> },
                ].map((item, i) => (
                  <div key={i} className="group">
                    <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1 block group-hover:text-purple-500 transition-colors uppercase">
                      {item.label}
                    </label>
                    <div className="flex items-center gap-3 text-gray-800">
                      <div className="text-purple-600/50 group-hover:text-purple-600 transition-colors">
                        {React.cloneElement(item.icon, { size: 18 })}
                      </div>
                      <p className="text-lg font-bold">{item.value || 'Not Provided'}</p>
                    </div>
                  </div>
                ))}

                <div className="group">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1 block group-hover:text-purple-500 transition-colors">
                    Phone Number
                  </label>
                  <div className="flex items-center gap-3">
                    <Phone className="text-purple-600/50 group-hover:text-purple-600 transition-colors" size={18} />
                    {editMode ? (
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-gray-50 border-b-2 border-gray-100 focus:border-purple-600 px-2 py-1 outline-none font-bold text-lg transition-all"
                      />
                    ) : (
                      <p className="text-lg font-bold text-gray-800">{profile.phone || 'Enter phone number'}</p>
                    )}
                  </div>
                </div>

                {editMode && (
                  <div className="group md:col-span-2">
                    <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1 block group-hover:text-purple-500 transition-colors">
                      Profile Picture URL
                    </label>
                    <div className="flex items-center gap-3">
                      <Camera className="text-purple-600/50 group-hover:text-purple-600 transition-colors" size={18} />
                      <input
                        type="text"
                        name="profilePicture"
                        value={formData.profilePicture}
                        onChange={handleChange}
                        placeholder="https://example.com/photo.jpg"
                        className="w-full bg-gray-50 border-b-2 border-gray-100 focus:border-purple-600 px-2 py-1 outline-none font-bold text-sm transition-all"
                      />
                    </div>
                  </div>
                )}
              </div>

              {editMode && (
                <div className="mt-12 flex gap-4">
                  <button
                    onClick={handleUpdate}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg shadow-green-200 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" /> Save Changes
                  </button>
                </div>
              )}
            </div>

            {/* Activity History */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Clubs List */}
              <div className="bg-white rounded-[2rem] shadow-xl shadow-purple-900/5 p-8 border border-gray-100">
                <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  My Clubs
                </h3>
                <div className="space-y-4">
                  {profile.clubsJoined?.length > 0 ? profile.clubsJoined.map(club => (
                    <div
                      key={club._id}
                      onClick={() => navigate(`/club/${club._id}`)}
                      className="group flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-indigo-50 transition-all cursor-pointer border border-transparent hover:border-indigo-100"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center font-black text-indigo-600">
                          {club.name.charAt(0)}
                        </div>
                        <span className="font-bold text-gray-800">{club.name}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-600 transition-colors" />
                    </div>
                  )) : (
                    <p className="text-gray-400 text-sm italic py-4">Not joined any clubs yet.</p>
                  )}
                </div>
              </div>

              {/* Events List */}
              <div className="bg-white rounded-[2rem] shadow-xl shadow-purple-900/5 p-8 border border-gray-100">
                <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Registered Events
                </h3>
                <div className="space-y-4">
                  {profile.eventsRegistered?.length > 0 ? profile.eventsRegistered.map(event => (
                    <div
                      key={event._id}
                      onClick={() => navigate(`/event/${event._id}`)}
                      className="group flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-blue-50 transition-all cursor-pointer border border-transparent hover:border-blue-100"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-800 line-clamp-1">{event.name}</p>
                          <p className="text-[10px] uppercase font-black text-blue-400">{event.club?.name}</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-blue-600 transition-colors" />
                    </div>
                  )) : (
                    <p className="text-gray-400 text-sm italic py-4">No events registered yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Area - Stats & Quick Links */}
          <div className="space-y-8">
            <div className="bg-white rounded-[2rem] shadow-xl shadow-purple-900/5 p-8 border border-gray-100 text-center">
              <h4 className="text-gray-400 text-xs font-black uppercase tracking-widest mb-8">Platform Identity</h4>
              <div className="space-y-10">
                <div>
                  <div className="text-4xl font-black text-purple-600 mb-1">{profile.clubsJoined?.length || 0}</div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Clubs Joined</div>
                </div>
                <div className="h-px bg-gray-50 mx-8"></div>
                <div>
                  <div className="text-4xl font-black text-indigo-600 mb-1">{profile.eventsRegistered?.length || 0}</div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Events Registered</div>
                </div>
                <div className="h-px bg-gray-50 mx-8"></div>
                <div>
                  <div className="text-sm font-black text-gray-800 mb-1">{new Date(profile.createdAt).toLocaleDateString()}</div>
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Member Since</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-[2rem] p-8 text-white shadow-xl shadow-indigo-900/20">
              <h3 className="font-black text-xl mb-4 leading-tight">Need help with your profile?</h3>
              <p className="text-indigo-100 text-sm mb-8 leading-relaxed">
                Roll Number, Branch and Year were assigned during registration. If these are incorrect, please contact the platform admin.
              </p>
              <button className="w-full bg-white text-indigo-700 py-3 rounded-xl font-bold text-sm tracking-wide hover:bg-indigo-50 transition-colors">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
